import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export function useOrderDetailState({ order, authAxios, onUpdate, onClearSelection, formatPrice }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  const [assignDeliverer, setAssignDeliverer] = useState("");
  const [dispatchPhone, setDispatchPhone] = useState("");
  const [dispatchName, setDispatchName] = useState("");
  const [dispatching, setDispatching] = useState(false);
  const [lastNotification, setLastNotification] = useState(null);
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [swapDeliverer, setSwapDeliverer] = useState("");
  const [swapping, setSwapping] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [restaurantData, setRestaurantData] = useState(null);
  const [customerStats, setCustomerStats] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [editingItems, setEditingItems] = useState(false);
  const [localItems, setLocalItems] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [menuProducts, setMenuProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  const [discountType, setDiscountType] = useState("fixed");
  const [discountReason, setDiscountReason] = useState("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleFor, setScheduleFor] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await authAxiosRef.current.get("/restaurant");
        const data = res.data?.data || res.data;
        setRestaurantData(data);
        const methods = data?.payment_methods?.filter(m => m.active) || [
          { name: "Efectivo", active: true },
          { name: "Visa (Debito)", active: true },
          { name: "Mastercard (Credito)", active: true }
        ];
        setPaymentMethods(methods);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (order?.id) {
      authAxiosRef.current.get(`/orders/${order.id}/customer-stats`)
        .then(res => setCustomerStats(res.data))
        .catch(() => setCustomerStats(null));
    }
  }, [order?.id]);

  const startEditItems = () => { setLocalItems(order.items.map(i => ({ ...i }))); setEditingItems(true); };
  const removeItem = (idx) => setLocalItems(prev => prev.filter((_, i) => i !== idx));
  const changeQty = (idx, delta) => setLocalItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it));
  const addProductToOrder = (product) => {
    setLocalItems(prev => {
      const existing = prev.findIndex(it => it.product_id === product.id);
      if (existing >= 0) return prev.map((it, i) => i === existing ? { ...it, quantity: it.quantity + 1 } : it);
      return [...prev, { product_id: product.id, product_name: product.name, quantity: 1, unit_price: product.price }];
    });
    setShowAddProduct(false);
  };
  const saveItemChanges = async () => {
    if (localItems.length === 0) { toast.error("O pedido deve ter pelo menos um item"); return; }
    try {
      const res = await authAxios.patch(`/orders/${order.id}/items`, { items: localItems });
      toast.success(`Items actualizados. Total: ${formatPrice(res.data.total)}`);
      setEditingItems(false);
      onUpdate();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || e.response?.statusText || e.message || "Erro ao atualizar itens";
      toast.error(errorMsg);
    }
  };
  const openAddProduct = async () => {
    if (menuProducts.length === 0) {
      try {
        const res = await authAxios.get("/menu/products");
        setMenuProducts(res.data.filter(p => p.active));
      } catch { toast.error("Erro ao carregar produtos"); }
    }
    setProductSearch("");
    setShowAddProduct(true);
  };
  const handleChangePayment = async (methodName) => {
    setUpdatingPayment(true);
    try {
      await authAxios.patch(`/orders/${order.id}/payment`, { payment_method: methodName });
      toast.success(`Pagamento atualizado: ${methodName}`);
      setShowPaymentDialog(false);
      onUpdate();
    } catch { toast.error("Erro ao atualizar pagamento"); }
    setUpdatingPayment(false);
  };
  const updateStatus = async (status, reason = "") => {
    if (status === "cancelled") { if (cancelling) return; setCancelling(true); }
    if ((status === "cancelled" || status === "delivered") && onClearSelection) onClearSelection();
    try {
      const params = new URLSearchParams({ status }); if (reason) params.set("reason", reason);
      await authAxios.patch(`/orders/${order.id}/status?${params.toString()}`);
      onUpdate();
    } catch (err) {
      if (err?.response?.status === 409) { toast.warning("Outro utilizador já atualizou este pedido. Atualizando dados..."); onUpdate(); }
      else { toast.error("Erro ao atualizar estado"); }
    } finally { if (status === "cancelled") setCancelling(false); }
  };
  const handleCancelConfirm = async () => { setShowCancelDialog(false); await updateStatus("cancelled", cancelReason.trim()); setCancelReason(""); };
  const handleFinalize = async () => {
    try { await authAxios.patch(`/orders/${order.id}/hide`); if (onClearSelection) onClearSelection(); onUpdate(); }
    catch { toast.error("Erro ao finalizar pedido"); }
  };
  const handleReverifyVivaPayment = async () => {
    try { await authAxios.post(`/orders/${order.id}/reverify-payment`); onUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || "Pagamento ainda não confirmado pela Viva"); }
  };
  const sendWhatsApp = async (notification) => {
    window.open(notification.whatsapp_url, "_blank");
    try { await authAxios.patch(`/notifications/${notification.id}/mark-sent`); setLastNotification(null); } catch {}
  };
  const handleAssignDeliverer = async () => {
    if (!assignDeliverer && !dispatchPhone.trim()) { toast.error("Selecione um entregador ou insira um telefone"); return; }
    setDispatching(true);
    try {
      const payload = assignDeliverer ? { deliverer_id: assignDeliverer } : { phone: dispatchPhone.trim(), name: dispatchName.trim() || "Estafeta" };
      const dispRes = await authAxios.post(`/deliverers/dispatch/${order.id}`, payload);
      if (dispRes.data?.success === false) { toast.error(dispRes.data?.error || "Erro ao despachar pedido"); setDispatching(false); return; }
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao despachar"); }
    setDispatching(false);
  };
  const handleQuickDispatch = async (delivererId, delivererName = "") => {
    console.log("[MANUAL_DISPATCH_CLICKED]", { orderId: order.id, delivererId, delivererName });
    setDispatching(true);
    try {
      const qRes = await authAxios.post(`/deliverers/dispatch/${order.id}`, { deliverer_id: delivererId });
      if (qRes.data?.success === false) { toast.error(qRes.data?.error || "Erro ao despachar pedido"); setDispatching(false); return; }
      if (delivererName) toast.success(`Pedido despachado para ${delivererName}`);
      onUpdate();
    }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao despachar"); }
    setDispatching(false);
  };
  const handleSwapDeliverer = async () => {
    if (!swapDeliverer) { toast.error("Selecione um entregador"); return; }
    setSwapping(true);
    try { await authAxios.patch(`/orders/${order.id}/swap-deliverer?new_deliverer_id=${swapDeliverer}`); setShowSwapDialog(false); setSwapDeliverer(""); onUpdate(); }
    catch { toast.error("Erro ao alterar entregador"); }
    setSwapping(false);
  };
  const openEditDialog = () => { setEditName(order.customer_name || ""); setEditPhone(order.customer_phone || ""); setEditAddress(order.delivery_address || ""); setEditNotes(order.notes || ""); setShowEditDialog(true); };
  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try { await authAxios.patch(`/orders/${order.id}`, { customer_name: editName.trim(), customer_phone: editPhone.trim(), delivery_address: editAddress.trim(), notes: editNotes.trim() }); setShowEditDialog(false); onUpdate(); }
    catch { toast.error("Erro ao atualizar"); }
    setSavingEdit(false);
  };
  const handleDuplicate = () => {
    const items = (order.items || []).map(it => ({ product_id: it.product_id || "", product_name: it.product_name || "", quantity: it.quantity || 1, unit_price: it.unit_price || 0, notes: it.notes || "", modifiers: it.modifiers || [], selected_variations: it.selected_variations || [] }));
    if (!items.length) return;
    const prefillData = {
      source: order.source || "manual",
      customer_name: order.customer_name || "",
      customer_phone: order.customer_phone || "",
      delivery_type: order.order_type || (order.delivery_address ? "delivery" : "counter"),
      delivery_address: order.delivery_address || ""
    };
    const cartPrefillData = { items, timestamp: Date.now() };
    localStorage.setItem("kaiso_prefill_order", JSON.stringify(prefillData));
    localStorage.setItem("kaiso_duplicate_cart_prefill", JSON.stringify(cartPrefillData));
    window.location.href = "/orders/new";
  };
  const handleSaveSchedule = async () => {
    if (!scheduleFor) { toast.error("Selecione uma data e hora"); return; }
    setSavingSchedule(true);
    try { await authAxios.patch(`/orders/${order.id}`, { scheduled_for: new Date(scheduleFor).toISOString() }); setShowScheduleDialog(false); onUpdate(); }
    catch { toast.error("Erro ao agendar pedido"); }
    setSavingSchedule(false);
  };
  const handlePrint = async (printReceiptFn) => {
    try {
      const savedMethod = localStorage.getItem("kaiso_print_method");
      if (savedMethod === "desktop") {
        await authAxios.post("/print/queue", { order_id: order.id, station: "default", copies: 1 });
        return { success: true, method: "desktop" };
      }
      const station = restaurantData?.print_stations?.[0] || {};
      return await printReceiptFn(order, station, restaurantData);
    } catch (e) { toast.error("Erro ao imprimir: " + (e.message || e)); }
  };

  return {
    assignDeliverer, setAssignDeliverer, dispatchPhone, setDispatchPhone, dispatchName, setDispatchName, dispatching, lastNotification, setLastNotification,
    showSwapDialog, setShowSwapDialog, swapDeliverer, setSwapDeliverer, swapping, showPaymentDialog, setShowPaymentDialog, paymentMethods, updatingPayment,
    restaurantData, customerStats, cancelling, showCancelDialog, setShowCancelDialog, cancelReason, setCancelReason, editingItems, setEditingItems, localItems, setLocalItems,
    showAddProduct, setShowAddProduct, menuProducts, productSearch, setProductSearch, showEditDialog, setShowEditDialog, editName, setEditName, editPhone, setEditPhone,
    editAddress, setEditAddress, editNotes, setEditNotes, savingEdit, showDiscountForm, setShowDiscountForm, discountInput, setDiscountInput, discountType, setDiscountType,
    discountReason, setDiscountReason, applyingDiscount, setApplyingDiscount, showScheduleDialog, setShowScheduleDialog, scheduleFor, setScheduleFor, savingSchedule,
    startEditItems, removeItem, changeQty, addProductToOrder, saveItemChanges, openAddProduct, handleChangePayment, updateStatus, handleCancelConfirm, handleFinalize,
    handleReverifyVivaPayment, sendWhatsApp, handleAssignDeliverer, handleQuickDispatch, handleSwapDeliverer, openEditDialog, handleSaveEdit, handleDuplicate, handleSaveSchedule, handlePrint
  };
}

